import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/rentPaymentsEdit.module.css';
import { rentPaymentsService } from '../services/rentPaymentsService';
import Loader from '../components/Loader';

const RentPaymentsEdit = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const getRoomId = () => {
    if (roomId) return roomId;
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('room_id') || '1';
  };
  const currentRoomId = getRoomId();

  const [room, setRoom] = useState(null);
  const [property, setProperty] = useState(null);
  const [rents, setRents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payments, setPayments] = useState({});
  const [disabledRents, setDisabledRents] = useState([]);
  const [action, setAction] = useState("Change Rent");

  useEffect(() => {
    loadRentPaymentsData();
  }, [currentRoomId]);

  const loadRentPaymentsData = async () => {
    try {
      setLoading(true);
      const data = await rentPaymentsService.getRentPaymentsEditData(currentRoomId);
      
      setRoom(data.room);
      setProperty(data.property);
      setRents(data.rents || []);
      setDisabledRents(data.disabledRents || []);
      setAction(data.action || "Change Rent");
      
      // Initialize payments with current values
      const initialPayments = {};
      data.rents?.forEach(rent => {
        initialPayments[rent.guardian.id] = {
          license_fee: rent.license_fee || 0,
          damage_security_payment: rent.damage_security_payment || 0,
          pay_gas: rent.pay_gas || 0,
          pay_electric: rent.pay_electric || 0,
          pay_water: rent.pay_water || 0,
          pay_council_tax: rent.pay_council_tax || 0
        };
      });
      setPayments(initialPayments);
    } catch (error) {
      console.error('Error loading rent payments data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentChange = (guardianId, field, value) => {
    setPayments(prev => ({
      ...prev,
      [guardianId]: {
        ...prev[guardianId],
        [field]: value
      }
    }));
  };

  const calculatePaymentSummary = (guardianId) => {
    const guardianPayments = payments[guardianId] || {};
    const licenseFee = parseFloat(guardianPayments.license_fee) || 0;
    const damageSecurityPayment = parseFloat(guardianPayments.damage_security_payment) || 0;
    return licenseFee + damageSecurityPayment;
  };

  // Update payment summary when payments change
  useEffect(() => {
    rents.forEach(rent => {
      const summaryElement = document.getElementById(`payment-summary-${rent.guardian.id}`);
      if (summaryElement) {
        summaryElement.textContent = `£${calculatePaymentSummary(rent.guardian.id)}`;
      }
    });
  }, [payments, rents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (Object.keys(payments).length === 0) {
      toast.error("No payment data to submit");
      return;
    }

    // Confirm action like Laravel
    const confirmMessage = "Are you sure you want to set payment?";
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSubmitting(true);
    try {
      await rentPaymentsService.updateRentPayments(currentRoomId, payments);
      toast.success("You updated the payments!");
      // Laravel Parity: redirect()->route('guardians.index')
      navigate('/guardians');
    } catch (error) {
      console.error('Error updating rent payments:', error);
      toast.error(error.message || "Failed to update rent payments");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const isRentDisabled = (rentId) => {
    return disabledRents.includes(rentId);
  };

  if (loading) return <Loader />;
  if (!room) return <div className={styles.errorContainer}>Room not found</div>;

  return (
    <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
      <div className={styles.container}>
        {/* Breadcrumbs - Laravel Style */}
        <div className={styles.breadcrumbContainer}>
          <nav aria-label="breadcrumb" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/dashboard" style={{ color: '#0a7280', textDecoration: 'none', fontSize: '14px' }}>Dashboard</Link>
            <span style={{ margin: '0 8px', color: '#666' }}>/</span>
            <Link to="/guardians" style={{ color: '#0a7280', textDecoration: 'none', fontSize: '14px' }}>Guardians</Link>
            <span style={{ margin: '0 8px', color: '#666' }}>/</span>
            {rents.length > 0 && (
              <>
                <Link
                  to={`/guardians/${rents[0].guardian?.id}`}
                  style={{ color: '#0a7280', textDecoration: 'none', fontSize: '14px' }}
                >
                  {rents[0].guardian?.full_name} ({rents[0].guardian?.unique_guardian_number})
                </Link>
                <span style={{ margin: '0 8px', color: '#666' }}>/</span>
              </>
            )}
            <span style={{
              backgroundColor: '#30C693',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '3px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              Change Rent
            </span>
          </nav>
        </div>

        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panelTitle} ${styles.titleStandard} ${styles.mb10}`}>
              <h3>{action}</h3>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Guardian Sections - Side by Side */}
              <div className={styles.row}>
                {rents.map((rent, key) => (
                  <div key={rent.id} className={`${styles.colXs12} ${styles.colSm6}`}>
                    <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                      <h3>Guardian #{key + 1}</h3>
                    </div>
                    <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown} ${styles.guardiansPayments}`} 
                         id={`guardian-${rent.guardian.id}`} 
                         data-id={rent.guardian.id}>
                      <div className={styles.colMd9}>
                        <dl className={`${styles.list} ${styles.assignGuardian} ${styles.greenListWithWidthSmaller} ${styles.pb40}`} style={{ marginTop: '10px' }}>
                          <dt>Name:</dt>
                          <dd>{rent.guardian.full_name} ({rent.guardian.unique_guardian_number})</dd>
                          <dt>Property:</dt>
                          <dd>
                            <span id={`property-guardian-${rent.guardian.id}`} className="property-name">
                              {property?.name || rent.property?.name}
                            </span>
                          </dd>
                          <dt>Status:</dt>
                          <dd><b>{rent.guardian.status.name}</b></dd>
                        </dl>

                        {/* Payment Fields */}
                        <div className={styles.textCenter} style={{ color: 'grey', marginBottom: '20px' }}>
                          Suggested Room Price is: {rent.room?.price} £
                        </div>

                        <fieldset className={`${styles.formHorizontal} ${styles.jsPaymentModule}`}>
                          {/* Table Headers */}
                          <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm6}`}>Name</label>
                            <div className={styles.colSm2}>
                              <div className={styles.mt5}>Old</div>
                            </div>
                            <div className={styles.colSm4}>
                              <div className={styles.mt5}>New</div>
                            </div>
                          </div>

                          {/* Monthly License Fee */}
                          <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm6} ${styles.labelRequired}`}>
                              Monthly License Fee *
                            </label>
                            <div className={styles.colSm2}>
                              <div className={styles.mt5}>£{rent.license_fee || 0}</div>
                            </div>
                            <div className={styles.colSm4}>
                              <input
                                type="number"
                                className={`${styles.formControl} ${styles.jsPaymentPart}`}
                                value={payments[rent.guardian.id]?.license_fee || ''}
                                onChange={(e) => handlePaymentChange(rent.guardian.id, 'license_fee', e.target.value)}
                                disabled={isRentDisabled(rent.id)}
                                required
                              />
                            </div>
                          </div>

                          {/* Key Payment */}
                          <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm6} ${styles.labelRequired}`}>
                              Key Payment *
                            </label>
                            <div className={styles.colSm2}>
                              <div className={styles.mt5}>£{rent.damage_security_payment || 0}</div>
                            </div>
                            <div className={styles.colSm4}>
                              <input
                                type="number"
                                className={`${styles.formControl} ${styles.jsPaymentPart}`}
                                value={payments[rent.guardian.id]?.damage_security_payment || ''}
                                onChange={(e) => handlePaymentChange(rent.guardian.id, 'damage_security_payment', e.target.value)}
                                disabled={isRentDisabled(rent.id)}
                                required
                              />
                            </div>
                          </div>

                          {/* Payment Summary */}
                          <div className={`${styles.formGroup} ${styles.paymentGroup} ${styles.paymentSummaryGroup}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm6}`}>
                              Summary
                            </label>
                            <div className={styles.colSm2}>
                              <div style={{ marginTop: '7px' }}>£{rent.payment || 0}</div>
                            </div>
                            <div className={styles.colSm4}>
                              <div id={`payment-summary-${rent.guardian.id}`} className={styles.paymentSummary}>
                                £{calculatePaymentSummary(rent.guardian.id)}
                              </div>
                            </div>
                          </div>
                        </fieldset>

                        {/* Utilities Section */}
                        <p className={styles.mt10}>Accept the liability to pay the following (tick if applicable):</p>

                        {/* Gas */}
                        <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                          <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.textLeft} ${styles.utilityLabel}`}>
                            Gas
                          </label>
                          <div className={styles.colSm9}>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_gas]`}
                              value="0"
                              checked={payments[rent.guardian.id]?.pay_gas === 0}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_gas', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> <span style={{ marginRight: '15px' }}>No</span>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_gas]`}
                              value="1"
                              checked={payments[rent.guardian.id]?.pay_gas === 1}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_gas', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> Yes
                          </div>
                        </div>

                        {/* Electric */}
                        <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                          <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.textLeft} ${styles.utilityLabel}`}>
                            Electric
                          </label>
                          <div className={styles.colSm9}>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_electric]`}
                              value="0"
                              checked={payments[rent.guardian.id]?.pay_electric === 0}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_electric', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> <span style={{ marginRight: '15px' }}>No</span>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_electric]`}
                              value="1"
                              checked={payments[rent.guardian.id]?.pay_electric === 1}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_electric', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> Yes
                          </div>
                        </div>

                        {/* Water */}
                        <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                          <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.textLeft} ${styles.utilityLabel}`}>
                            Water
                          </label>
                          <div className={styles.colSm9}>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_water]`}
                              value="0"
                              checked={payments[rent.guardian.id]?.pay_water === 0}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_water', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> <span style={{ marginRight: '15px' }}>No</span>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_water]`}
                              value="1"
                              checked={payments[rent.guardian.id]?.pay_water === 1}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_water', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> Yes
                          </div>
                        </div>

                        {/* Council Tax */}
                        <div className={`${styles.formGroup} ${styles.paymentGroup}`}>
                          <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.textLeft} ${styles.utilityLabel}`}>
                            Council Tax
                          </label>
                          <div className={styles.colSm9}>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_council_tax]`}
                              value="0"
                              checked={payments[rent.guardian.id]?.pay_council_tax === 0}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_council_tax', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> <span style={{ marginRight: '15px' }}>No</span>
                            <input
                              type="radio"
                              name={`payments[${rent.guardian.id}][pay_council_tax]`}
                              value="1"
                              checked={payments[rent.guardian.id]?.pay_council_tax === 1}
                              onChange={(e) => handlePaymentChange(rent.guardian.id, 'pay_council_tax', parseInt(e.target.value))}
                              disabled={isRentDisabled(rent.id)}
                              className={styles.formCheckbox}
                            /> Yes
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Property Information - Full Width */}
              <div className={styles.row}>
                <div className={styles.colMd12}>
                  <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                    <h3>Property</h3>
                  </div>
                  <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                    <div className={styles.colSm6}>
                      <dl className={`${styles.list} ${styles.greenList} ${styles.greenListWithWidth}`}>
                        <dt>Property ID:</dt>
                        <dd>{property?.property_id}</dd>
                        <dt>Property Name:</dt>
                        <dd>{property?.name}</dd>
                        <dt>Property Status:</dt>
                        <dd>{property?.status_text}</dd>
                        <dt>Address:</dt>
                        <dd>{property?.address_1}</dd>
                        <dt>Postcode:</dt>
                        <dd>{property?.postcode}</dd>
                        <dt>Lease Dates:</dt>
                        <dd>
                          {formatDate(property?.date_taken_on)}
                          {property?.date_taken_back && ` to ${formatDate(property?.date_taken_back)}`}
                        </dd>
                        <dt>Room:</dt>
                        <dd>{room?.name}</dd>
                      </dl>
                    </div>
                    <div className={styles.colSm6}>
                      <dl className={`${styles.list} ${styles.greenList} ${styles.greenListWithWidth}`}>
                        <dt>Property Notes:</dt>
                        <dd>{property?.short_description || '-'}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className={`${styles.formGroup} ${styles.textRight}`}>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                  disabled={submitting}
                  data-confirm-action="Are you sure you want to set payment?"
                >
                  {submitting ? "Updating..." : "Set payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentPaymentsEdit; 